<template>
  <div class="machine-learning">
    <div class="machine-learning-content">
      <div class="header-section">
        <h1 class="page-title">Machine Learning</h1>
        <p class="page-subtitle">Browse machine learning evaluation outputs by disease, sex, and model type.</p>
        <p class="page-description">
          First select the disease category, specific disease endpoint, sex group, and model type, and then click Run to browse the corresponding machine-learning model and its evaluation results, including discrimination metrics and visualizations such as ROC and precision–recall curves, predicted-risk distributions, and confusion matrices. Four prediction algorithms are available: penalized logistic regression, XGBoost, LightGBM, and multilayer perceptron. In addition to browsing precomputed models, MetabHub provides an online prediction function for user-supplied data. Users can download the sample CSV to prepare metabolomic profiles in the required format, upload the completed file, select the desired model and sex mode, and click Predict. The server then applies the corresponding pre-trained model and returns individual-level predicted probabilities for the selected disease.
        </p>
      </div>

      <div class="query-card">
        <div class="filters-section">
          <div class="filter-group">
            <label class="filter-label">Disease Category</label>
            <el-select
              v-model="selectedCategory"
              placeholder="Select category"
              class="filter-select"
              size="small"
              filterable
            >
              <el-option
                v-for="category in categories"
                :key="category"
                :label="category"
                :value="category"
              />
            </el-select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Disease</label>
            <el-select
              v-model="selectedDisease"
              placeholder="Select disease"
              class="filter-select"
              size="small"
              filterable
              :filter-method="filterDiseases"
            >
              <el-option
                v-for="item in filteredDiseases"
                :key="item.phid"
                :label="item.reportedTrait"
                :value="item.phid"
              />
            </el-select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Sex</label>
            <el-select v-model="sex" size="small" class="filter-select">
              <el-option label="All" value="all" />
              <el-option label="Female" value="female" />
              <el-option label="Male" value="male" />
            </el-select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Model Type</label>
            <el-select v-model="modelType" size="small" class="filter-select">
              <el-option
                v-for="item in modelTypes"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
          </div>

          <div class="filter-actions">
            <el-button
              type="danger"
              size="small"
              class="run-btn"
              :loading="running"
              @click="handleRun"
            >
              <i class="el-icon-video-play"></i>
              Run
            </el-button>
          </div>
        </div>
      </div>

      <div class="results-section" v-if="hasSearched">
        <div class="panel-card">
          <div class="panel-header">
            <div>
              <div class="panel-title">Metrics Comparison</div>
            </div>
            <div class="result-actions">
              <div class="table-summary">{{ metricsTotal }} results</div>
              <el-button size="small" @click="handleDownloadMetrics">Download</el-button>
            </div>
          </div>

          <el-table
            v-loading="metricsLoading"
            :data="metricsData"
            border
            stripe
            class="result-table"
            header-cell-class-name="table-header-cell"
            empty-text="No data"
            @sort-change="handleMetricsSortChange"
          >
            <el-table-column
              v-for="column in metricsColumns"
              :key="column.key"
              :prop="column.key"
              :label="column.label"
              min-width="140"
              show-overflow-tooltip
              sortable="custom"
              :sort-orders="sortOrders"
            />
          </el-table>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <div>
              <div class="panel-title">Model Resources</div>
            </div>
          </div>

          <div v-loading="resourcesLoading" class="images-grid">
            <div class="image-card" v-for="resource in resources" :key="resource.name">
              <div class="image-name">{{ resource.name }}</div>
              <el-image
                v-if="resource.type === 'image'"
                class="result-image"
                :src="`data:image/png;base64,${resource.data}`"
                :alt="resource.name"
                :preview-src-list="resources.filter(item => item.type === 'image').map(item => `data:image/png;base64,${item.data}`)"
                fit="contain"
              />
              <div v-else class="resource-placeholder">
                Unsupported resource type: {{ resource.type }}
              </div>
            </div>
            <div class="empty-block" v-if="!resourcesLoading && resources.length === 0">No resources</div>
          </div>
        </div>

        <div class="panel-card">
          <div class="panel-header">
            <div>
              <div class="panel-title">Confusion Matrix</div>
            </div>
            <div class="result-actions">
              <div class="table-summary">
                Showing {{ confusionData.length > 0 ? (confusionPage - 1) * confusionPageSize + 1 : 0 }}
                to {{ Math.min(confusionPage * confusionPageSize, confusionTotal) }} of {{ confusionTotal }} results
              </div>
              <el-button size="small" @click="handleDownloadConfusionMatrix">Download</el-button>
            </div>
          </div>

          <el-table
            v-loading="confusionLoading"
            :data="confusionData"
            border
            stripe
            class="result-table"
            header-cell-class-name="table-header-cell"
            empty-text="No data"
            @sort-change="handleConfusionSortChange"
          >
            <el-table-column
              v-for="column in confusionColumns"
              :key="column.key"
              :prop="column.key"
              :label="column.label"
              min-width="140"
              show-overflow-tooltip
              sortable="custom"
              :sort-orders="sortOrders"
            />
          </el-table>

          <div class="pagination-wrap">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="confusionPage"
              :page-size="confusionPageSize"
              :total="confusionTotal"
              @current-change="handleConfusionPageChange"
            />
          </div>
        </div>
      </div>

      <div class="panel-card upload-panel">
        <div class="panel-header">
          <div>
            <div class="panel-title">CSV Prediction Upload</div>
            <div class="panel-subtitle">Upload individual metabolite CSV data for prediction.</div>
          </div>
          <a
            class="sample-file-link sample-file-link-header"
            href="/static/database/sample.csv"
            target="_blank"
            rel="noopener noreferrer"
          >
            Download sample CSV
          </a>
        </div>

        <div class="upload-form">
          <div class="filter-group">
            <label class="filter-label">Disease ID</label>
            <el-input
              :value="selectedDisease"
              placeholder="Select disease above"
              size="small"
              disabled
            />
          </div>

          <div class="filter-group">
            <label class="filter-label">Model Type</label>
            <el-select v-model="uploadModelType" size="small" class="filter-select">
              <el-option
                v-for="item in modelTypes"
                :key="`upload-${item}`"
                :label="item"
                :value="item"
              />
            </el-select>
          </div>

          <div class="filter-group">
            <label class="filter-label">Sex Mode</label>
            <el-select v-model="sexMode" size="small" class="filter-select" clearable placeholder="auto">
              <el-option
                v-for="item in sexModeOptions"
                :key="item.value"
                :label="item.label"
                :value="item.value"
              />
            </el-select>
          </div>

          <div class="filter-group upload-file-row">
            <label class="filter-label">CSV File</label>
            <el-upload
              class="upload-block"
              action=""
              :auto-upload="false"
              :show-file-list="true"
              :limit="1"
              accept=".csv,text/csv"
              :file-list="uploadFileList"
              :on-change="handleUploadFileChange"
              :on-remove="handleUploadFileRemove"
            >
              <el-button size="small">Choose CSV</el-button>
            </el-upload>
            <p class="sample-file-hint">Please refer to the sample CSV file for the required upload format and column structure.</p>
          </div>

          <div class="filter-actions upload-actions">
            <el-button
              type="danger"
              size="small"
              class="predict-btn"
              :loading="uploading"
              @click="handlePredictUpload"
            >
              Predict
            </el-button>
          </div>
        </div>

        <div v-if="predictResults.length > 0 || predictResponseText" class="upload-result">
          <div class="upload-result-title">Prediction Response</div>
          <el-table
            v-if="predictResults.length > 0"
            :data="predictResults"
            border
            stripe
            class="result-table predict-result-table"
            header-cell-class-name="table-header-cell"
            empty-text="No prediction results"
          >
            <el-table-column
              v-for="column in predictResultColumns"
              :key="column.key"
              :prop="column.key"
              :label="column.label"
              min-width="160"
              show-overflow-tooltip
            />
          </el-table>
          <pre v-else class="upload-result-content">{{ predictResponseText }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import request, { downloadFile } from '@/utils/request'

export default {
  name: 'MachineLearning',
  data() {
    return {
      categories: [],
      diseases: [],
      diseaseKeyword: '',
      selectedCategory: '',
      selectedDisease: '',
      sex: 'all',
      modelType: 'xgboost',
      uploadModelType: 'xgboost',
      sexMode: 'auto',
      modelTypes: ['xgboost', 'mlp', 'logistic_regression', 'lightgbm'],
      sexModeOptions: [
        { label: 'Auto', value: 'auto' },
        { label: 'Female', value: 'female' },
        { label: 'Male', value: 'male' }
      ],
      hasSearched: false,
      running: false,
      uploading: false,
      metricsLoading: false,
      resourcesLoading: false,
      confusionLoading: false,
      metricsColumns: [],
      metricsData: [],
      rawMetricsData: [],
      metricsTotal: 0,
      resources: [],
      confusionColumns: [],
      confusionData: [],
      sortOrders: ['descending', 'ascending', null],
      metricsSortKey: '',
      metricsSortOrder: '',
      confusionSortKey: '',
      confusionSortOrder: '',
      uploadFile: null,
      uploadFileList: [],
      predictResults: [],
      predictResponseText: '',
      confusionPage: 1,
      confusionPageSize: 10,
      confusionTotal: 0,
      confusionPages: 0
    }
  },
  computed: {
    filteredDiseases() {
      if (!this.diseaseKeyword) {
        return this.diseases
      }
      const keyword = this.diseaseKeyword.toLowerCase()
      return this.diseases.filter(item =>
        (item.reportedTrait && item.reportedTrait.toLowerCase().includes(keyword)) ||
        (item.phid && item.phid.toLowerCase().includes(keyword))
      )
    },
    predictResultColumns() {
      if (this.predictResults.length === 0) return []
      return Object.keys(this.predictResults[0]).map(key => ({
        key,
        label: key
      }))
    }
  },
  watch: {
    selectedCategory(value) {
      if (value) {
        this.loadDiseases(value)
      } else {
        this.diseases = []
      }
      this.selectedDisease = ''
      this.diseaseKeyword = ''
    }
  },
  mounted() {
    this.loadCategories()
    this.loadModelTypes()
  },
  methods: {
    async loadCategories() {
      try {
        const data = await request.get('/api/machine-learning/categories')
        this.categories = data || []
      } catch (error) {
        console.error('Failed to load machine learning categories:', error)
      }
    },
    async loadDiseases(category) {
      try {
        const data = await request.get('/api/machine-learning/diseases', {
          params: { category }
        })
        this.diseases = data || []
      } catch (error) {
        console.error('Failed to load machine learning diseases:', error)
      }
    },
    async loadModelTypes() {
      try {
        const data = await request.get('/api/machine-learning/model-types')
        if (Array.isArray(data) && data.length > 0) {
          this.modelTypes = data
          if (!data.includes(this.modelType)) {
            this.modelType = data[0]
          }
          if (!data.includes(this.uploadModelType)) {
            this.uploadModelType = data[0]
          }
        }
      } catch (error) {
        console.error('Failed to load model types:', error)
      }
    },
    filterDiseases(keyword) {
      this.diseaseKeyword = keyword
    },
    async handleRun() {
      if (!this.selectedCategory || !this.selectedDisease || !this.sex || !this.modelType) {
        this.$message.warning('Please select Disease Category, Disease, Sex and Model Type.')
        return
      }

      this.hasSearched = true
      this.confusionPage = 1
      this.metricsSortKey = ''
      this.metricsSortOrder = ''
      this.confusionSortKey = ''
      this.confusionSortOrder = ''
      this.running = true
      try {
        await Promise.all([
          this.loadMetrics(),
          this.loadResources(),
          this.loadConfusionMatrix()
        ])
      } finally {
        this.running = false
      }
    },
    async loadMetrics() {
      this.metricsLoading = true
      try {
        const data = await request.get('/api/machine-learning/metrics', {
          params: {
            phid: this.selectedDisease,
            sex: this.sex
          }
        })
        const headers = data.headers || []
        const records = data.records || []
        this.metricsColumns = headers.map(header => ({
          key: header,
          label: header
        }))
        this.rawMetricsData = records.map(row => this.rowToObject(headers, row))
        this.metricsData = this.sortMetricsData(this.rawMetricsData)
        this.metricsTotal = data.total || 0
      } catch (error) {
        console.error('Failed to load machine learning metrics:', error)
        this.metricsColumns = []
        this.rawMetricsData = []
        this.metricsData = []
        this.metricsTotal = 0
      } finally {
        this.metricsLoading = false
      }
    },
    async handleDownloadMetrics() {
      if (!this.selectedCategory || !this.selectedDisease || !this.sex) {
        this.$message.warning('Please select Disease Category, Disease and Sex.')
        return
      }
      await downloadFile('/api/machine-learning/metrics/download', {
        phid: this.selectedDisease,
        sex: this.sex
      }, `machine_learning_metrics_${this.selectedDisease}_${this.sex}.csv`)
    },
    async loadResources() {
      this.resourcesLoading = true
      try {
        const data = await request.get('/api/machine-learning/resources', {
          params: {
            phid: this.selectedDisease,
            sex: this.sex,
            modelType: this.modelType
          }
        })
        this.resources = data || []
      } catch (error) {
        console.error('Failed to load machine learning resources:', error)
        this.resources = []
      } finally {
        this.resourcesLoading = false
      }
    },
    async loadConfusionMatrix() {
      this.confusionLoading = true
      try {
        const data = await request.get('/api/machine-learning/confusion-matrix/page', {
          params: {
            phid: this.selectedDisease,
            sex: this.sex,
            modelType: this.modelType,
            pageNum: this.confusionPage,
            pageSize: this.confusionPageSize,
            sortBy: this.confusionSortKey || undefined,
            sortOrder: this.confusionSortOrder || undefined
          }
        })
        const headers = data.headers || []
        const records = data.records || []
        this.confusionColumns = headers.map(header => ({
          key: header,
          label: header
        }))
        this.confusionData = records.map(row => this.rowToObject(headers, row))
        this.confusionTotal = data.total || 0
        this.confusionPages = data.pages || 0
      } catch (error) {
        console.error('Failed to load confusion matrix:', error)
        this.confusionColumns = []
        this.confusionData = []
        this.confusionTotal = 0
        this.confusionPages = 0
      } finally {
        this.confusionLoading = false
      }
    },
    handleConfusionPageChange(page) {
      if (page < 1 || page > this.confusionPages || page === this.confusionPage) {
        return
      }
      this.confusionPage = page
      this.loadConfusionMatrix()
    },
    handleMetricsSortChange({ prop, order }) {
      this.metricsSortKey = order ? prop : ''
      this.metricsSortOrder = this.normalizeElementSortOrder(order)
      this.metricsData = this.sortMetricsData(this.rawMetricsData)
    },
    handleConfusionSortChange({ prop, order }) {
      this.confusionSortKey = order ? prop : ''
      this.confusionSortOrder = this.normalizeElementSortOrder(order)
      this.confusionPage = 1
      this.loadConfusionMatrix()
    },
    normalizeElementSortOrder(order) {
      if (order === 'ascending') return 'asc'
      if (order === 'descending') return 'desc'
      return ''
    },
    sortMetricsData(data) {
      const source = Array.isArray(data) ? data.slice() : []
      if (!this.metricsSortKey || !this.metricsSortOrder) {
        return source
      }
      const sorted = source.slice().sort((left, right) => {
        return this.compareSortableValues(left[this.metricsSortKey], right[this.metricsSortKey])
      })
      return this.metricsSortOrder === 'desc' ? sorted.reverse() : sorted
    },
    compareSortableValues(left, right) {
      const leftNumber = this.parseSortableNumber(left)
      const rightNumber = this.parseSortableNumber(right)
      if (leftNumber !== null && rightNumber !== null) {
        return leftNumber - rightNumber
      }
      return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' })
    },
    parseSortableNumber(value) {
      if (value === null || value === undefined) return null
      const normalized = String(value).trim()
      if (!normalized || normalized.toUpperCase() === 'NA') return null
      const parsed = Number(normalized)
      return Number.isNaN(parsed) ? null : parsed
    },
    async handleDownloadConfusionMatrix() {
      if (!this.selectedCategory || !this.selectedDisease || !this.sex || !this.modelType) {
        this.$message.warning('Please select Disease Category, Disease, Sex and Model Type.')
        return
      }
      await downloadFile('/api/machine-learning/confusion-matrix/download', {
        phid: this.selectedDisease,
        sex: this.sex,
        modelType: this.modelType
      }, `machine_learning_confusion_matrix_${this.selectedDisease}_${this.sex}_${this.modelType}.tsv`)
    },
    handleUploadFileChange(file, fileList) {
      this.uploadFile = file.raw || null
      this.uploadFileList = fileList.slice(-1)
    },
    handleUploadFileRemove() {
      this.uploadFile = null
      this.uploadFileList = []
    },
    async handlePredictUpload() {
      if (!this.selectedDisease) {
        this.$message.warning('Please select Disease above before uploading.')
        return
      }
      if (!this.uploadModelType) {
        this.$message.warning('Please select Model Type.')
        return
      }
      if (!this.uploadFile) {
        this.$message.warning('Please choose a CSV file.')
        return
      }

      const formData = new FormData()
      formData.append('file', this.uploadFile)
      formData.append('disease_id', this.selectedDisease)
      formData.append('model_type', this.uploadModelType)
      if (this.sexMode) {
        formData.append('sex_mode', this.sexMode)
      }

      this.uploading = true
      this.predictResults = []
      this.predictResponseText = ''
      try {
        const response = await request.post('/api/machine-learning/predict', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })
        const results = this.extractPredictResults(response)
        if (results.length > 0) {
          this.predictResults = results
        } else {
          this.predictResponseText = typeof response === 'string'
            ? response
            : JSON.stringify(response, null, 2)
        }
      } catch (error) {
        console.error('Failed to upload prediction CSV:', error)
      } finally {
        this.uploading = false
      }
    },
    extractPredictResults(response) {
      if (!response) return []
      if (Array.isArray(response.results)) return response.results
      if (response.data && Array.isArray(response.data.results)) return response.data.results
      return []
    },
    rowToObject(headers, row) {
      const item = {}
      headers.forEach((header, index) => {
        item[header] = row[index]
      })
      return item
    }
  }
}
</script>

<style lang="less" scoped>
.machine-learning {
  min-height: 100%;
  background:
    radial-gradient(circle at top left, rgba(248, 113, 113, 0.16), transparent 28%),
    linear-gradient(180deg, #fff7f7 0%, #fff 28%, #fef2f2 100%);
}

.machine-learning-content {
  margin: 0 auto;
  padding: 36px 28px 48px;
}

.header-section {
  margin-bottom: 24px;
}

.page-title {
  margin: 0;
  font-size: 32px;
  font-weight: 700;
  color: #7f1d1d;
}

.page-subtitle {
  margin: 10px 0 0;
  color: #7f1d1d;
  opacity: 0.78;
  font-size: 15px;
}

.page-description {
  width: 100%;
  max-width: none;
  margin: 12px 0 0;
  color: #7f1d1d;
  opacity: 0.84;
  font-size: 14px;
  line-height: 1.75;
  text-align: left;
}

.query-card,
.panel-card {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(248, 113, 113, 0.18);
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(127, 29, 29, 0.08);
}

.query-card {
  padding: 22px 24px;
}

.filters-section {
  display: grid;
  grid-template-columns: repeat(4, minmax(180px, 1fr)) auto;
  gap: 16px;
  align-items: end;
}

.filter-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.filter-label {
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
}

.filter-select {
  width: 100%;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
}

.upload-form {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr)) auto;
  gap: 16px;
  align-items: end;
}

.upload-file-row {
  grid-column: 1 / -1;
}

.upload-block {
  display: flex;
  align-items: center;
  min-height: 40px;
}

.sample-file-link {
  display: inline-flex;
  align-items: center;
  color: #991b1b;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
}

.sample-file-link-header {
  margin-top: 2px;
  padding: 8px 12px;
  border-radius: 999px;
  background: #fff7f7;
  border: 1px solid rgba(153, 27, 27, 0.14);
  white-space: nowrap;
}

.sample-file-link:hover {
  color: #b91c1c;
  text-decoration: underline;
}

.sample-file-hint {
  margin-top: -4px;
  color: #6b7280;
  font-size: 12px;
  line-height: 1.5;
}

.upload-actions {
  grid-column: 1 / -1;
  align-self: end;
  justify-content: center;
}

.run-btn {
  min-width: 110px;
}

.predict-btn {
  min-width: 120px;
}

.results-section {
  margin-top: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.upload-panel {
  margin-top: 24px;
}

.panel-card {
  padding: 20px 20px 18px;
}

.panel-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 16px;
}

.panel-title {
  font-size: 18px;
  font-weight: 700;
  color: #7f1d1d;
}

.panel-subtitle {
  margin-top: 6px;
  color: #6b7280;
  font-size: 13px;
  line-height: 1.5;
}

.panel-subtitle,
.table-summary {
  font-size: 13px;
  color: #6b7280;
}

.result-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

/deep/ .result-actions .el-button {
  padding: 9px 16px;
  background: linear-gradient(135deg, #fff1f2 0%, #ffe4e6 100%);
  color: #991b1b;
  border: 1px solid #fecaca;
  border-radius: 10px;
  box-shadow: 0 6px 16px rgba(239, 68, 68, 0.12);
  font-weight: 600;
}

/deep/ .result-actions .el-button:hover,
/deep/ .result-actions .el-button:focus {
  background: linear-gradient(135deg, #fee2e2 0%, #fecdd3 100%);
  border-color: #fca5a5;
  color: #7f1d1d;
}

.images-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 18px;
  min-height: 120px;
}

.image-card {
  border: 1px solid #fecaca;
  border-radius: 14px;
  padding: 14px;
  background: linear-gradient(180deg, #fff, #fff7f7);
}

.image-name {
  margin-bottom: 12px;
  font-size: 13px;
  font-weight: 600;
  color: #991b1b;
  word-break: break-all;
}

.result-image {
  display: block;
  width: 100%;
  border-radius: 10px;
  background: #fff;
}

:deep(.result-image .el-image__inner) {
  width: 100%;
  height: auto;
  border-radius: 10px;
  object-fit: contain;
}

.resource-placeholder {
  padding: 18px 12px;
  border-radius: 10px;
  background: #fff5f5;
  color: #991b1b;
  font-size: 13px;
  word-break: break-all;
}

.result-table {
  width: 100%;
}

.pagination-wrap {
  margin-top: 18px;
  display: flex;
  justify-content: flex-end;
}

.empty-block {
  padding: 28px 0;
  text-align: center;
  color: #9ca3af;
  grid-column: 1 / -1;
}

.upload-result {
  margin-top: 18px;
  border-top: 1px solid #fee2e2;
  padding-top: 18px;
}

.upload-result-title {
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 700;
  color: #991b1b;
}

.predict-result-table {
  min-height: 240px;
}

.upload-result-content {
  margin: 0;
  padding: 18px;
  border-radius: 12px;
  background: #fff7f7;
  color: #7f1d1d;
  font-size: 13px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  overflow: auto;
  min-height: 240px;
  max-height: 520px;
}

/deep/ .table-header-cell {
  background: #fef2f2 !important;
  color: #7f1d1d;
  font-weight: 700;
}

/deep/ .el-input__inner:focus,
/deep/ .el-select .el-input.is-focus .el-input__inner {
  border-color: #ef4444;
}

/deep/ .el-pagination.is-background .el-pager li:not(.disabled).active {
  background-color: #ef4444;
}

@media (max-width: 1200px) {
  .filters-section {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .upload-form {
    grid-template-columns: repeat(2, minmax(180px, 1fr));
  }

  .filter-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 768px) {
  .machine-learning-content {
    padding: 24px 16px 36px;
  }

  .filters-section {
    grid-template-columns: 1fr;
  }

  .upload-form {
    grid-template-columns: 1fr;
  }

  .panel-header {
    flex-direction: column;
  }

  .pagination-wrap {
    justify-content: center;
  }
}
</style>
